import Heading from '@atlaskit/heading';
import { isBugBountyParticipant } from '@atlassian/amkt-common';
import { CloudFortifiedStatusValues } from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { AppTileHighlightedText } from '@atlassian/mpac-ui';
import React from 'react';
import { getBootstrapLinks } from '../../Config';
import { TEST_IDS } from '../../primitives/testIds';
import { createTileHighlights } from '../../utils/createTileHighlights';
import { CertificationBadge } from '../CertificationBadge/CertificationBadge';
import { InstallDownloadMetrics } from '../InstallDownloadMetrics';
import { PartnerDetails } from '../PartnerDetails/PartnerDetails';
import { Ratings } from '../Ratings/Ratings';
import { AppLogo, AppLogoWrapper, AppNameWrapper, AppTileContainer, AppTileHeader, AppTileMainContentWrapper, NoCertification, PartnerDetailsWrapper, RatingsAndInstallsContainer, Tagline } from './AppTile.styles';
import { BadgeVariant, Certification } from './AppTile.types';
import { shouldOpenPartnerLinkInNewTab } from './AppTile.utils';
import { AppTileBadge } from './AppTileBadge';
// eslint-disable-next-line max-lines-per-function
export function AppTile(_ref) {
  let {
    tileData,
    environment,
    source,
    hideTagline = false,
    onTileClick
  } = _ref;
  const links = getBootstrapLinks(environment);
  const appLogoUrl = getAppLogoUrl();
  const appName = getAppName();
  const tagLine = getTagLine();
  const certification = getCertification();
  const tileBadgeVariant = getTileBadgeVariant();
  const showTagline = !hideTagline;
  const {
    downloads,
    activeInstalls: installs,
    bundled
  } = tileData.distribution;
  const partner = tileData.vendor;
  const partnerDetailProps = getPartnerDetailProps();
  function getAppLogoUrl() {
    const imageId = tileData.logo.highRes || tileData.logo.image;
    return links.mediaFile.expand({
      imageId
    });
  }
  function getAppName() {
    if (tileData._highlightResult) {
      const highlightedText = createTileHighlights(tileData._highlightResult, 'name', 'tokenizedName');
      return /*#__PURE__*/React.createElement(AppTileHighlightedText, {
        highlightedText: highlightedText,
        originalText: tileData.name,
        overrideStyles: {
          color: "var(--ds-text-selected)",
          fontWeight: 500
        }
      });
    } else {
      return tileData.name;
    }
  }
  function getTagLine() {
    if (tileData._highlightResult) {
      const highlightedText = createTileHighlights(tileData._highlightResult, 'tagLine', 'tokenizedTagLine');
      return /*#__PURE__*/React.createElement(AppTileHighlightedText, {
        highlightedText: highlightedText,
        originalText: tileData.tagLine,
        overrideStyles: {
          color: "var(--ds-text-selected)",
          fontWeight: 500
        }
      });
    } else {
      return tileData.tagLine;
    }
  }
  function getTileBadgeVariant() {
    let badgeVariant;
    if (tileData.marketingLabels.includes('Featured')) {
      badgeVariant = BadgeVariant.FEATURED;
    }
    return badgeVariant;
  }
  function getPartnerDetailProps() {
    var _partner$partnerTier;
    const partnerPageUrl = links.partnerDetails.expand({
      id: partner.id
    });
    return {
      name: partner.name,
      link: partnerPageUrl,
      partnerTier: (_partner$partnerTier = partner.partnerTier) === null || _partner$partnerTier === void 0 ? void 0 : _partner$partnerTier.type.toUpperCase(),
      openPartnerLinkInNewTab: shouldOpenPartnerLinkInNewTab(source)
    };
  }
  function getCertification() {
    var _tileData$programs, _tileData$programs$cl, _tileData$programs2;
    const isCloudFortifiedApp = ((_tileData$programs = tileData.programs) === null || _tileData$programs === void 0 ? void 0 : (_tileData$programs$cl = _tileData$programs.cloudFortified) === null || _tileData$programs$cl === void 0 ? void 0 : _tileData$programs$cl.status) === CloudFortifiedStatusValues.Approved;
    const isCloudSecurityParticipantApp = isBugBountyParticipant(AtlassianProductHostingType.Cloud, (_tileData$programs2 = tileData.programs) === null || _tileData$programs2 === void 0 ? void 0 : _tileData$programs2.bugBountyParticipant) && tileData.deployments.includes('cloud');
    let certificationType;
    if (isCloudFortifiedApp) {
      certificationType = Certification.CLOUD_FORTIFIED;
    } else if (isCloudSecurityParticipantApp) {
      certificationType = Certification.CLOUD_SECURITY_PARTICIPANT;
    }
    return certificationType;
  }
  return /*#__PURE__*/React.createElement(AppTileContainer, {
    tabIndex: 0,
    onClick: () => onTileClick === null || onTileClick === void 0 ? void 0 : onTileClick(tileData),
    "data-testid": TEST_IDS.APP_TILE
  }, tileBadgeVariant && /*#__PURE__*/React.createElement(AppTileBadge, {
    variant: tileBadgeVariant
  }), /*#__PURE__*/React.createElement(AppTileMainContentWrapper, null, /*#__PURE__*/React.createElement(AppTileHeader, null, /*#__PURE__*/React.createElement(AppLogoWrapper, null, /*#__PURE__*/React.createElement(AppLogo, {
    alt: tileData.name,
    loading: "lazy",
    src: appLogoUrl
  })), /*#__PURE__*/React.createElement(AppNameWrapper, null, /*#__PURE__*/React.createElement(Heading, {
    size: "medium",
    as: "h3"
  }, appName))), /*#__PURE__*/React.createElement(PartnerDetailsWrapper, null, /*#__PURE__*/React.createElement(PartnerDetails, partnerDetailProps)), showTagline && /*#__PURE__*/React.createElement(Tagline, null, tagLine)), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(RatingsAndInstallsContainer, null, /*#__PURE__*/React.createElement(Ratings, {
    ratings: tileData.ratings
  }), /*#__PURE__*/React.createElement(InstallDownloadMetrics, {
    installs: installs,
    downloads: downloads,
    isPreinstalled: bundled
  })), certification ? /*#__PURE__*/React.createElement(CertificationBadge, {
    certification: certification
  }) : /*#__PURE__*/React.createElement(NoCertification, null)));
}